<template>
  <div :class="t.wrapperLoose">
    <label :class="t.label">
      {{ question.prompt }}
      <span v-if="!question.required" class="text-gray-400 font-normal">(optional)</span>
    </label>
    <p
      v-if="question.subtext"
      :class="t.subtext"
      v-html="question.subtext"
    ></p>
    <p :class="t.helperText">Select all that apply</p>

    <!-- Options as tappable buttons -->
    <div :class="t.wrapperLoose">
      <button
        v-for="option in question.answers"
        :key="option"
        @click="toggleOption(option)"
        type="button"
        :class="t.optionButton"
        :style="buttonStyle(option)"
        @mouseenter="hoveredOption = option"
        @mouseleave="hoveredOption = null"
      >
        <!-- Checkbox indicator -->
        <span
          :class="[t.optionIndicator, 'rounded border-2']"
          :style="checkboxStyle(option)"
        >
          <svg
            v-if="isSelected(option)"
            class="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
            style="color: var(--team-text)"
          >
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
        <span class="font-medium">{{ option }}</span>
      </button>
    </div>

    <!-- Error Message -->
    <p v-if="error" :class="t.error">
      Please select at least one option.
    </p>
  </div>
</template>

<script>
import { tokensFor } from './size_tokens';

export default {
  name: 'QuestionMultiSelect',
  props: {
    question: {
      type: Object,
      required: true,
    },
    existingAnswer: {
      type: Object,
      default: null,
    },
    size: {
      type: String,
      default: 'kiosk',
      validator: (v) => ['kiosk', 'form'].includes(v),
    },
  },
  data() {
    return {
      answer: [],
      originalAnswer: [],
      error: false,
      hoveredOption: null,
    };
  },
  computed: {
    t() {
      return tokensFor(this.size);
    },
    dirty() {
      // Compare arrays
      if (this.answer.length !== this.originalAnswer.length) return true;
      return this.answer.some((a) => !this.originalAnswer.includes(a));
    },
    valid() {
      if (this.question.required) {
        return this.answer.length > 0;
      }
      return true;
    },
  },
  watch: {
    answer: {
      deep: true,
      handler() {
        this.emit();
        if (this.valid) {
          this.error = false;
        }
      },
    },
  },
  created() {
    if (this.existingAnswer && this.existingAnswer.answer) {
      // Answer is stored as array
      const existing = this.existingAnswer.answer;
      if (Array.isArray(existing)) {
        this.answer = [...existing];
        this.originalAnswer = [...existing];
      }
    }
    this.$notificationManager?.on('show-reg-errors', this.validate);
  },
  destroyed() {
    this.$notificationManager?.off('show-reg-errors', this.validate);
  },
  methods: {
    isSelected(option) {
      return this.answer.includes(option);
    },
    buttonStyle(option) {
      const isSelected = this.isSelected(option);
      const isHovered = this.hoveredOption === option;

      if (isSelected) {
        return {
          backgroundColor: 'var(--team-primary)',
          borderColor: 'var(--team-primary)',
          color: 'var(--team-text)',
        };
      }
      if (isHovered) {
        return {
          borderColor: 'var(--team-primary)',
          backgroundColor: '#F9FAFB',
          color: '#374151',
        };
      }
      return {
        borderColor: '#D1D5DB',
        backgroundColor: 'white',
        color: '#374151',
      };
    },
    checkboxStyle(option) {
      const isSelected = this.isSelected(option);
      if (isSelected) {
        return {
          borderColor: 'var(--team-text)',
          backgroundColor: 'var(--team-primary)',
        };
      }
      return {
        borderColor: '#D1D5DB',
        backgroundColor: 'white',
      };
    },
    toggleOption(option) {
      const index = this.answer.indexOf(option);
      if (index === -1) {
        this.answer = [...this.answer, option];
      } else {
        this.answer = this.answer.filter((a) => a !== option);
      }
    },
    emit() {
      this.$emit('answer-changed', this.question, this.answer, this.dirty, this.valid);
    },
    validate() {
      this.error = !this.valid;
    },
  },
};
</script>
